import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Archive, Hammer } from "lucide-react";
import { StrikeChip, StrikeHint, useTicketStrikes } from "./StrikeChip";
import { buildOverlayTicket } from "../lib/exactaOverlays";
import { saveTicket } from "../lib/ticketHistory";
import { todayLocalIso } from "../lib/dateHelpers";

const fmtDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return y && m && d
    ? new Date(y, m - 1, d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : String(iso);
};

const monoStyle = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const fmt$ = (n) => (Math.round(n * 100) / 100).toFixed(2);
const inputCls =
  "w-24 px-2 h-8 rounded bg-[#0a0a0a] text-stone-100 text-xs border border-stone-700 focus:border-[#84cc16] focus:outline-none";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.2em] text-stone-500">
    {label}
    {children}
  </label>
);

const TicketLines = ({ ticket, strikes, live }) => (
  <div className="flex flex-col gap-2" data-testid="overlay-ticket-lines">
    {ticket.groups.map((g) => {
      const lineOff = strikes.isStruck("line", g.first);
      const n = live.find((x) => x.first === g.first)?.seconds.length || 0;
      return (
        <div key={g.first} className="flex items-center gap-2 flex-wrap" data-testid={`overlay-ticket-line-${g.first}`}>
          <StrikeChip
            n={g.first}
            testId={`overlay-ticket-first-${g.first}`}
            struck={lineOff}
            onToggle={() => strikes.toggle("line", g.first)}
          />
          <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500">with</span>
          {g.seconds.map((s) => (
            <StrikeChip
              key={s}
              n={s}
              testId={`overlay-ticket-second-${g.first}-${s}`}
              struck={lineOff || strikes.isStruck(`L${g.first}`, s)}
              onToggle={lineOff ? undefined : () => strikes.toggle(`L${g.first}`, s)}
            />
          ))}
          <span className="ml-auto text-[10px] text-stone-400" data-testid={`overlay-ticket-line-cost-${g.first}`}>
            {n} × ${fmt$(ticket.base)} = ${fmt$(n * ticket.base)}
          </span>
        </div>
      );
    })}
  </div>
);

export const OverlayTicketBuilder = ({ cells, overlayCount, trackName, trackSlug, race, date }) => {
  const [base, setBase] = useState("1");
  const [count, setCount] = useState("");
  const [ticket, setTicket] = useState(null);
  const strikes = useTicketStrikes(ticket);
  const live = (ticket?.groups || [])
    .map((g) => ({
      first: g.first,
      seconds: strikes.isStruck("line", g.first) ? [] : strikes.keep(`L${g.first}`, g.seconds),
    }))
    .filter((g) => g.seconds.length > 0);
  const liveCombos = live.reduce((a, g) => a + g.seconds.length, 0);
  const struckCount = (ticket?.groups || []).reduce(
    (a, g) => a + (strikes.isStruck("line", g.first) ? 1 : strikes.countIn(`L${g.first}`, g.seconds)),
    0
  );

  // Clear only on a real change of race — not on refresh blips where props briefly go empty.
  const raceKey = useRef(null);
  useEffect(() => {
    if (!trackSlug || !race) return;
    const k = `${trackSlug}|${race}`;
    if (raceKey.current && raceKey.current !== k) setTicket(null);
    raceKey.current = k;
  }, [trackSlug, race]);

  const build = () => {
    const b = parseFloat(base);
    const n = parseInt(count || overlayCount, 10);
    if (!(b > 0) || !(n > 0)) {
      toast.error("Enter a base wager and a number of combinations.");
      return;
    }
    const t = buildOverlayTicket(cells, n);
    if (!t.combos) {
      toast.warning("No priced combinations yet — press Capture Now first.");
      return;
    }
    setTicket({ ...t, base: b, asked: n });
  };

  const archive = () => {
    if (!liveCombos) {
      toast.error("Every combo is crossed out — restore at least one to archive.");
      return;
    }
    live.forEach((g) =>
      saveTicket({
        kind: "Exacta",
        shape: "overlay",
        track: trackName || "",
        track_slug: trackSlug || "",
        race: race || "",
        date: date || "",
        base: ticket.base,
        combos: g.seconds.length,
        cost: fmt$(g.seconds.length * ticket.base),
        horses: [
          { label: "1st Position", posts: [g.first] },
          { label: "2nd Position", posts: g.seconds },
        ],
      })
    );
    toast.success(`Overlay ticket archived as ${live.length} Exacta ticket${live.length === 1 ? "" : "s"} · $${fmt$(liveCombos * ticket.base)}`);
    setTicket(null);
  };

  return (
    <div className="rounded-md border border-stone-800 bg-[#0a0a0a] p-4 space-y-3" style={monoStyle} data-testid="overlay-ticket-builder">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#84cc16] font-bold">Build Overlay Ticket</span>
        <span
          className="text-[10px] px-2 py-0.5 rounded bg-[#1a2e05] text-[#a3e635] border border-[#365314] font-bold uppercase tracking-[0.14em]"
          data-testid="overlay-ticket-stamp"
        >
          {fmtDate(date || todayLocalIso())} · {trackName || "—"} · R{race || "?"}
        </span>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <Field label="Base wager $">
          <input data-testid="overlay-ticket-base" type="number" min="0.1" step="0.1" value={base} onChange={(e) => setBase(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Combinations">
          <input data-testid="overlay-ticket-count" type="number" min="1" step="1" value={count} placeholder={String(overlayCount || "")} onChange={(e) => setCount(e.target.value)} className={inputCls} />
        </Field>
        <button
          type="button"
          onClick={build}
          data-testid="overlay-ticket-build"
          className="inline-flex items-center gap-2 px-3 h-8 rounded border border-[#365314] bg-[#1a2e05] hover:bg-[#84cc16] hover:text-[#0a0a0a] text-[#a3e635] text-[11px] uppercase tracking-[0.18em] font-bold transition-colors"
        >
          <Hammer size={14} /> Build
        </button>
      </div>
      {ticket && (
        <div className="space-y-3 pt-2 border-t border-stone-800" data-testid="overlay-ticket">
          <StrikeHint count={struckCount} onClear={strikes.clear} testId="overlay-ticket-restore" />
          <TicketLines ticket={ticket} strikes={strikes} live={live} />
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-stone-300">
            <span data-testid="overlay-ticket-summary">
              {live.length} tickets · {liveCombos} combos · ${fmt$(liveCombos * ticket.base)}
            </span>
            {ticket.combos > ticket.overlays && (
              <span className="text-stone-500" data-testid="overlay-ticket-filler-note">
                {ticket.overlays} overlays + {ticket.combos - ticket.overlays} next best
              </span>
            )}
            {ticket.combos < ticket.asked && (
              <span className="text-[#facc15]" data-testid="overlay-ticket-short-note">
                only {ticket.combos} priced combos available
              </span>
            )}
            <button
              type="button"
              onClick={archive}
              data-testid="overlay-ticket-archive"
              className="ml-auto inline-flex items-center gap-2 px-3 h-8 rounded border border-stone-600 hover:border-[#84cc16] hover:text-[#84cc16] text-stone-200 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors"
            >
              <Archive size={14} /> Archive
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
